import { computed, Injectable, signal, effect } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, of } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpService } from '@shared/services/http.service';
import { environment as env } from 'environments/environment';
import { AVATARS_ROUTE, PROFILE_ROUTE, WEBSITE_API_URL } from '@shared/constants/constants';
import { Profile } from '@shared/models/profile';
import { EntityTypes } from 'app/core/models/general-entity.types';
import { ROLES } from '@constants/role.constants';

export interface Avatar {
  value: string,
  url: string
} 

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  profile = signal<Profile | null>(null);
  
  profileResource = rxResource({
    stream: () =>
      this.getProfile().pipe(
        // Keep app bootstrap resilient when profile API is unavailable (e.g. local test runs).
        catchError(() => of({ success: false, data: null }))
      )
  });

  private readonly avatarsResource = rxResource({
    stream: () =>
      this.httpService
        .get<any>(env.baseUrl + WEBSITE_API_URL + AVATARS_ROUTE, { skipLoader: true })
        .pipe(catchError(() => of({ success: false, data: [] })))
  });

  readonly avatars = computed<Avatar[]>(() => (this.avatarsResource.value()?.data as Avatar[]) ?? []);

  constructor(private readonly httpService: HttpService) {
    effect(() => {
      const res = this.profileResource.value();
      if (res?.success) {
        this.profile.set(res.data);
      }
    });
  }

  saveUpdates(body: any): Observable<any> {
    const url = env.baseUrl + WEBSITE_API_URL + PROFILE_ROUTE;
    return this.httpService.put(url, body);
  }

  getProfile(): Observable<any> {
    const url = env.baseUrl + WEBSITE_API_URL + PROFILE_ROUTE;
    return this.httpService.get<any>(url);
  }

  setProfile(profile: any | null) {
    this.profile.set(profile);
  }

  getProfileImageUrl(entity: EntityTypes) {
    return computed(() => {
      let imageUrl = null;
      if (this.profile()?.imageUrl ?? '') {
        imageUrl = this.profile()?.imageUrl ?? '';
        return imageUrl;
      } 
      
      if (this.profile()?.avatarUrl ?? '') {
        imageUrl = this.profile()?.avatarUrl ?? '';
        return imageUrl;
      }

      if (this.avatars().length > 0 ) {
        if (entity === ROLES.CANDIDATE) {
          // TODO GET FROM PROFILE
          const gender = 'other'
          imageUrl = this.avatars()?.find(av => av.value === gender)?.url ?? '';
        } else {
          imageUrl = this.avatars()?.find(av => av.value === 'employer')?.url ?? '';
        }
      }
      return imageUrl || 'https://placehold.co/52x52';
    });
  };
}
